import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { Box, Button, Chip, IconButton, Paper, Stack, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DataTable from "components/common/tables/DataTable";
import { usePageTitle } from "context/PageTitleContext";
import { fetchUsers } from "../../services/user.service";
import {
  catalogApi,
  normalizeExecutionGroup,
  normalizeExecutionGroupUser,
  normalizeResponseError,
} from "../../services/catalogs/systemCatalog.service";
import CatalogFormDialog from "./CatalogFormDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import { AppButton } from "components/common/buttons";

const groupFields = [
  { name: "subcontractExecutionGroupCode", label: "Mã nhóm" },
  { name: "subcontractExecutionGroupName", label: "Tên nhóm giao khoán", required: true },
  { name: "isActive", label: "Đang hoạt động", type: "switch", defaultValue: true },
  { name: "notes", label: "Ghi chú", multiline: true },
];

const GroupUsersPanel = ({ group, userOptions, onSnackbar, onReloadGroups }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState({ open: false, mode: "create", row: null });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fields = useMemo(
    () => [
      { name: "userId", label: "Người dùng", required: true, type: "select", options: userOptions },
      { name: "isLeader", label: "Trưởng nhóm", type: "switch", defaultValue: false },
      { name: "displayOrder", label: "Thứ tự hiển thị", type: "number", defaultValue: 0 },
      { name: "notes", label: "Ghi chú", multiline: true },
    ],
    [userOptions]
  );

  const dialogFields = useMemo(
    () =>
      fields.map((field) =>
        field.name === "userId" ? { ...field, multiple: formState.mode === "create" } : field
      ),
    [fields, formState.mode]
  );

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await catalogApi.getGroupUsers(group.subcontractExecutionGroupId);
      setRows(data.map(normalizeExecutionGroupUser));
    } catch (error) {
      setRows([]);
      onSnackbar(normalizeResponseError(error, "Không thể tải danh sách user trong nhóm"), "error");
    } finally {
      setLoading(false);
    }
  }, [group.subcontractExecutionGroupId, onSnackbar]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSubmit = async (values) => {
    const userIds = Array.isArray(values.userId) ? values.userId : [values.userId];
    if (formState.mode !== "edit" && Boolean(values.isLeader) && userIds.length > 1) {
      onSnackbar("Chỉ 1 người dùng là trưởng nhóm", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const buildPayload = (userId) => ({
        subcontractExecutionGroupId: group.subcontractExecutionGroupId,
        userId: Number(userId),
        isLeader: Boolean(values.isLeader),
        displayOrder: Number(values.displayOrder || 0),
        notes: String(values.notes || "").trim() || null,
      });
      if (formState.mode === "edit") {
        await catalogApi.updateGroupUser(formState.row.subcontractExecutionGroupUserId, buildPayload(userIds[0]));
        onSnackbar("Cập nhật user trong nhóm thành công");
      } else {
        await Promise.all(
          userIds.map((userId) => catalogApi.addGroupUser(group.subcontractExecutionGroupId, buildPayload(userId)))
        );
        onSnackbar("Thêm người dùng vào nhóm thành công");
      }
      setFormState({ open: false, mode: "create", row: null });
      await Promise.all([loadUsers(), onReloadGroups()]);
    } catch (error) {
      onSnackbar(normalizeResponseError(error, "Không thể lưu người dùng vào trong nhóm"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await catalogApi.deleteGroupUser(deleteTarget.subcontractExecutionGroupUserId);
      setDeleteTarget(null);
      onSnackbar("Xóa người dùng khỏi nhóm thành công");
      await Promise.all([loadUsers(), onReloadGroups()]);
    } catch (error) {
      onSnackbar(normalizeResponseError(error, "Không thể xóa người dùng khỏi nhóm"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBeforeDialogChange = useCallback(
    (name, value, nextValues) => {
      if (formState.mode === "edit") return true;

      const selectedUserIds = Array.isArray(nextValues.userId) ? nextValues.userId : [nextValues.userId].filter(Boolean);
      const isMultiLeader = Boolean(nextValues.isLeader) && selectedUserIds.length > 1;
      if ((name === "isLeader" || name === "userId") && isMultiLeader) {
        onSnackbar("Chỉ 1 người dùng là trưởng nhóm", "warning");
        return false;
      }

      return true;
    },
    [formState.mode, onSnackbar]
  );

  const columns = useMemo(
    () => [
      { accessorKey: "userName", header: "Tên đăng nhập", size: 100 },
      { accessorKey: "fullName", header: "Họ và tên", size: 180, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "email", header: "Email", size: 160, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "roleName", header: "Vai trò", size: 130, Cell: ({ cell }) => cell.getValue() || "-" },
      {
        accessorKey: "isLeader",
        header: "Trưởng nhóm",
        size: 90,
        // Cell: ({ cell }) => (cell.getValue() ? <Chip label="Có" color="primary" size="small" /> : "-"),
        Cell: ({ cell }) =>
          cell.getValue() ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            "-"
          ),
      },
      // { accessorKey: "displayOrder", header: "Thứ tự", size: 90 },
      { accessorKey: "notes", header: "Ghi chú", size: 260, Cell: ({ cell }) => cell.getValue() || "-" },
    ],
    []
  );

  return (
    <Box sx={{ p: 1.5, backgroundColor: "#f8fafc", borderTop: "1px solid #e5edf5" }}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1}>
          <Chip label={`Người dùng trong ${group.subcontractExecutionGroupName}`} size="small" sx={{ alignSelf: { xs: "flex-start", sm: "center" } }} />
        </Stack>
        <DataTable
          columns={columns}
          data={rows}
          enableRowActions
          enablePagination={false}
          enableBottomToolbar={false}
          maxTableHeight="320px"
          state={{ isLoading: loading, showProgressBars: submitting }}
          renderTopToolbarExtraActions={() => (
              <AppButton variant="outlined" startIcon={<AddIcon />} size="small" onClick={() => setFormState({ open: true, mode: "create", row: null })}>
                Thêm người dùng
              </AppButton>
          )}
          renderRowActions={({ row }) => (
            <Stack direction="row" spacing={0.5} justifyContent="center">
              <Tooltip title="Sửa">
                <IconButton size="small" color="primary" onClick={() => setFormState({ open: true, mode: "edit", row: row.original })}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Xóa">
                <IconButton size="small" color="error" onClick={() => setDeleteTarget(row.original)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        />
      </Stack>

      <CatalogFormDialog
        open={formState.open}
        title={formState.mode === "edit" ? "Cập nhật người dùng trong nhóm" : "Thêm người dùng vào nhóm"}
        fields={dialogFields}
        initialValues={formState.row}
        submitting={submitting}
        onClose={() => setFormState({ open: false, mode: "create", row: null })}
        onBeforeChange={handleBeforeDialogChange}
        onSubmit={handleSubmit}
      />
      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        message="Bạn có chắc chắn muốn xóa người dùng này khỏi nhóm?"
        submitting={submitting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </Box>
  );
};

const SubcontractExecutionGroupsManagement = () => {
  const { setPageTitle } = usePageTitle();
  const [rows, setRows] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState({ open: false, mode: "create", row: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [groups, usersResponse] = await Promise.all([
        catalogApi.list("/SubcontractExecutionGroup"),
        fetchUsers(1000, 1, null, "FullName", "ASC", true),
      ]);
      setRows(groups.map(normalizeExecutionGroup));
      setUserOptions(
        (usersResponse?.items || []).map((item) => ({
          value: item.userId ?? item.UserId,
          label: `${item.fullName || item.FullName || item.userName || item.UserName} (${item.userName || item.UserName})`,
        }))
      );
    } catch (error) {
      setRows([]);
      showSnackbar(normalizeResponseError(error, "Không thể tải danh sách nhóm giao khoán"), "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    setPageTitle("Quản lý nhóm giao khoán");
  }, [setPageTitle]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        subcontractExecutionGroupCode: String(values.subcontractExecutionGroupCode || "").trim() || null,
        subcontractExecutionGroupName: String(values.subcontractExecutionGroupName || "").trim(),
        isActive: Boolean(values.isActive),
        notes: String(values.notes || "").trim() || null,
      };
      if (formState.mode === "edit") {
        await catalogApi.update("/SubcontractExecutionGroup", formState.row.subcontractExecutionGroupId, payload);
        showSnackbar("Cập nhật nhóm giao khoán thành công");
      } else {
        await catalogApi.create("/SubcontractExecutionGroup", payload);
        showSnackbar("Thêm nhóm giao khoán thành công");
      }
      setFormState({ open: false, mode: "create", row: null });
      await loadData();
    } catch (error) {
      showSnackbar(normalizeResponseError(error, "Không thể lưu nhóm giao khoán"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await catalogApi.remove("/SubcontractExecutionGroup", deleteTarget.subcontractExecutionGroupId);
      setDeleteTarget(null);
      showSnackbar("Xóa nhóm giao khoán thành công");
      await loadData();
    } catch (error) {
      showSnackbar(normalizeResponseError(error, "Không thể xóa nhóm giao khoán"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "subcontractExecutionGroupCode", header: "Mã nhóm", size: 80, Cell: ({ cell }) => cell.getValue() || "-" },
      { accessorKey: "subcontractExecutionGroupName", header: "Tên nhóm giao khoán", size: 240 },
      {
        accessorKey: "isActive",
        header: "Trạng thái",
        size: 90,
        Cell: ({ cell }) => (cell.getValue() ? <Chip label="Hoạt động" color="success" size="small" /> : <Chip label="Tạm dừng" size="small" />),
      },
      { accessorKey: "leaderFullName", header: "Trưởng nhóm", size: 180, Cell: ({ row }) => row.original.leaderFullName || row.original.leaderUserName || "-" },
      { accessorKey: "totalMembers", header: "Tổng nhân sự", size: 80 },
      { accessorKey: "notes", header: "Ghi chú", size: 320, Cell: ({ cell }) => cell.getValue() || "-" },
    ],
    []
  );

  return (
    <Box sx={{ p: 2, backgroundColor: "#f9fafb", minHeight: "calc(100vh - 64px)" }}>
      <Stack spacing={2}>
        <DataTable
          columns={columns}
          data={rows}
          enableRowActions
          enableExpanding
          renderDetailPanel={({ row }) => (
            <GroupUsersPanel
              group={row.original}
              userOptions={userOptions}
              onSnackbar={showSnackbar}
              onReloadGroups={loadData}
            />
          )}
          state={{ isLoading: loading, showProgressBars: submitting }}
          renderTopToolbarExtraActions={() => (
            <Tooltip title="Thêm phòng ban">
              <AppButton variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setFormState({ open: true, mode: "create", row: null })} disableElevation>
                Thêm nhóm giao khoán
              </AppButton>
            </Tooltip>
          )}
          renderRowActions={({ row }) => (
            <Stack direction="row" spacing={0.5} justifyContent="center">
              <Tooltip title="Sửa">
                <IconButton size="small" color="primary" onClick={() => setFormState({ open: true, mode: "edit", row: row.original })}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Xóa">
                <IconButton size="small" color="error" onClick={() => setDeleteTarget(row.original)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        />
      </Stack>

      <CatalogFormDialog
        open={formState.open}
        title={formState.mode === "edit" ? "Cập nhật nhóm giao khoán" : "Thêm nhóm giao khoán"}
        fields={groupFields}
        initialValues={formState.row}
        submitting={submitting}
        onClose={() => setFormState({ open: false, mode: "create", row: null })}
        onSubmit={handleSubmit}
      />
      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        message="Bạn có chắc chắn muốn xóa nhóm giao khoán này?"
        submitting={submitting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
      <AppSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        message={snackbar.message}
      />
    </Box>
  );
};

export default SubcontractExecutionGroupsManagement;
